import React from 'react';
import { useConfigContext } from '../../config/ConfigContext';
import {
  deleteFolder,
  deleteGallery,
  deletePanorama,
  deleteWebStandalone,
  generateNewFolderSharedAccessKey,
  generateNewGallerySharedAccessKey,
  generateNewPanoramaSharedAccessKey,
  generateNewWebstandaloneSharedAccessKey,
  getFolderHierarchy,
  moveFolder,
  moveGallery,
  movePanorama,
  moveWebStandalone,
  renameFolder,
  renameGallery,
  renamePanorama,
  renameWebStandalone,
  sendFolderSharedPerMail,
  sendGallerySharedPerMail,
  sendPanoramaSharedPerMail,
  sendWebstandaloneSharedPerMail,
} from '../services/UploadManagementService';
import {
  Content,
  ContentDisplayName,
  FolderContent,
  GalleryContent,
  getDetailFromContent,
  PanoramaContent,
  PanoramaDetails,
  WebStandaloneContent,
} from '../services/UploadManagementServiceTypes';
import { ContentActionsProps } from './ContentActions';
import { ContentGridItem } from './ContentGrid';
import { ContentListItem } from './ContentTable';
import {
  createSharedPanoramaUrl,
  createSharedWebstandaloneUrl,
  getFolderUrl,
  getGalleryUrl,
  getSharedGalleryUrl,
} from './getContentUrl';
import { useUploadContext } from './UploadManagementContext';

export interface ContentViewProps extends Omit<ContentActionsProps, 'menuLabel'> {
  content: Content;
  openContent: () => void;
  contentDisplayName: ContentDisplayName;
  thumbnail?: string;
}

export const ContentViewItem: React.FC<ContentViewProps> = (props) => {
  const { dataView } = useUploadContext();
  if (dataView === 'list') {
    return <ContentListItem {...props} />;
  }

  if (dataView === 'grid') {
    return <ContentGridItem {...props} />;
  }
  return null;
};

export const FolderView: React.FunctionComponent<{ content: FolderContent }> = ({ content }) => {
  const { view, getFolderListing } = useUploadContext();
  const { guid } = content;
  const handleOnClick = () => {
    const url = getFolderUrl(content, view);
    window.location.assign(url);
  };
  return (
    <ContentViewItem
      content={content}
      contentDisplayName={'Folder'}
      openContent={handleOnClick}
      deleteConfig={{
        objectName: content.name || '',
        deleteAction: () => deleteFolder(guid),
        description: 'The Folder will be permanently deleted.',
        onSuccess: getFolderListing,
      }}
      shareConfig={{
        createNewLink: async () => {
          const patchedFolder = await generateNewFolderSharedAccessKey(guid);
          return getFolderUrl(patchedFolder, 'shared');
        },
        sendLinkPerMail: (email) => sendFolderSharedPerMail({ email, guid }),
        initialLinkToShare: getFolderUrl(content, 'shared'),
      }}
      renameConfig={{
        onSuccess: getFolderListing,
        itemName: content.name || content.contentType.name,
        itemTypeName: content.contentType.name,
        maxLength: 50,
        renameAction: (newName) => renameFolder(guid, newName),
      }}
      moveConfig={{
        content,
        getFolderHierarchy: getFolderHierarchy,
        onSuccess: getFolderListing,
        moveAction: (targetParentGuid: string) => moveFolder(content.guid, targetParentGuid),
      }}
    />
  );
};

const getPanoramaDisplayType = (panoramaDetails: PanoramaDetails) =>
  panoramaDetails.isStereo ? 'Panorama Stereo' : 'Panorama Mono';

export const PanoramaView: React.FunctionComponent<{ content: PanoramaContent }> = ({
  content,
}) => {
  const config = useConfigContext();
  const { getFolderListing, contentDetails } = useUploadContext();
  const { guid } = content;
  const panoramaDetails = getDetailFromContent(content, contentDetails);
  const thumbnail = panoramaDetails.isStereo ? undefined : panoramaDetails.thumbnailDownloadUrl;
  const displayType = getPanoramaDisplayType(panoramaDetails);
  // TODO: use viewUrl from contentDetails instead
  const panoramaUrl = createSharedPanoramaUrl(content, config);

  const handleOnClick = () => {
    window.open(panoramaUrl);
  };

  return (
    <ContentViewItem
      content={content}
      contentDisplayName={displayType}
      thumbnail={thumbnail}
      openContent={handleOnClick}
      deleteConfig={{
        objectName: content.name || '',
        deleteAction: () => deletePanorama(guid),
        description: 'Panorama Exports will be removed from the cloud.',
        onSuccess: getFolderListing,
      }}
      renameConfig={{
        onSuccess: getFolderListing,
        itemName: content.name || content.contentType.name,
        itemTypeName: content.contentType.name,
        maxLength: 50,
        renameAction: (newName) => renamePanorama(guid, newName),
      }}
      shareConfig={{
        createNewLink: async () => {
          const patchedPanorama = await generateNewPanoramaSharedAccessKey(guid);
          return createSharedPanoramaUrl(patchedPanorama, config);
        },
        sendLinkPerMail: (email) => sendPanoramaSharedPerMail({ email, guid }),
        initialLinkToShare: panoramaUrl,
      }}
      moveConfig={{
        content,
        getFolderHierarchy: getFolderHierarchy,
        onSuccess: getFolderListing,
        moveAction: (newParentGuid: string) => movePanorama(content.guid, newParentGuid),
      }}
    />
  );
};

export const WebStandaloneView: React.FunctionComponent<{ content: WebStandaloneContent }> = ({
  content,
}) => {
  const { getFolderListing } = useUploadContext();
  const config = useConfigContext();
  const { guid } = content;
  // TODO: use viewUrl from contentDetails instead
  const webstandaloneUrl = createSharedWebstandaloneUrl(content, config);
  const handleOnClick = () => {
    window.open(webstandaloneUrl);
  };
  return (
    <ContentViewItem
      content={content}
      contentDisplayName={'Web Standalone'}
      openContent={handleOnClick}
      deleteConfig={{
        objectName: content.name || '',
        deleteAction: () => deleteWebStandalone(guid),
        description: 'Web Standalones will be deleted in Enscape.',
        onSuccess: getFolderListing,
      }}
      renameConfig={{
        onSuccess: getFolderListing,
        itemName: content.name || content.contentType.name,
        itemTypeName: content.contentType.name,
        maxLength: 50,
        renameAction: (newName) => renameWebStandalone(guid, newName),
      }}
      shareConfig={{
        createNewLink: async () => {
          const patchedWebstandalone = await generateNewWebstandaloneSharedAccessKey(guid);
          return createSharedWebstandaloneUrl(patchedWebstandalone, config);
        },
        sendLinkPerMail: (email) => sendWebstandaloneSharedPerMail({ email, guid }),
        initialLinkToShare: webstandaloneUrl,
      }}
      moveConfig={{
        content,
        getFolderHierarchy: getFolderHierarchy,
        onSuccess: getFolderListing,
        moveAction: (newParentGuid: string) => moveWebStandalone(content.guid, newParentGuid),
      }}
    />
  );
};

export const GalleryView: React.FunctionComponent<{ content: GalleryContent }> = ({ content }) => {
  const { getFolderListing } = useUploadContext();
  const { guid } = content;
  const gallerySharedLink = getSharedGalleryUrl(content);
  const galleryLink = getGalleryUrl(content);
  const handleOnClick = () => window.open(galleryLink);
  return (
    <ContentViewItem
      content={content}
      contentDisplayName={'Panorama Gallery'}
      openContent={handleOnClick}
      deleteConfig={{
        objectName: content.name || '',
        deleteAction: () => deleteGallery(guid),
        description: 'Panorama Gallery will be removed from the cloud.',
        onSuccess: getFolderListing,
      }}
      renameConfig={{
        onSuccess: getFolderListing,
        itemName: content.name || content.contentType.name,
        itemTypeName: content.contentType.name,
        maxLength: 50,
        renameAction: (newName) => renameGallery(guid, newName),
      }}
      moveConfig={{
        content,
        getFolderHierarchy: getFolderHierarchy,
        onSuccess: getFolderListing,
        moveAction: (newParentGuid: string) => moveGallery(content.guid, newParentGuid),
      }}
      shareConfig={{
        initialLinkToShare: getSharedGalleryUrl(content),
        createNewLink: async () => {
          const patchedGallery = await generateNewGallerySharedAccessKey(guid);
          return getSharedGalleryUrl(patchedGallery);
        },
        sendLinkPerMail: (email) => sendGallerySharedPerMail({ email, guid }),
      }}
      openViewConfig={{
        label: 'Open in View mode',
        onClick: () => window.open(gallerySharedLink),
      }}
    />
  );
};
